// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
import { getAbsoluteUrl } from "$utils/urls.js";
import { getDescriptionName } from "$utils/geo-names.js";
import { setContext, getContext, onMount } from "svelte";
import { parseDate, formatDate } from "$utils/dates.js";
import { GEO_LEVELS, FREQUENCIES, SERIES_ORDER, DEFAULT_METRIC } from "$utils/consts.js";
import { csvParse } from "d3-dsv";
import { formatValue, formatChange } from "$utils/formats.js";
import { idMap, getCdAbb } from "$utils/geo-names.js";
import { readParams, writeParams, validateParams } from "$utils/query-params.js";

// Computed once at module load — idMap is a static import that never changes at runtime.
const STATE_DROPDOWN_LIST = Array.from(idMap.values())
  .map(({ geoid, state_name }) => ({ value: geoid, label: state_name }))
  .toSorted((a, b) => {
    if (b.label === "US") return 1;
    return a.label.localeCompare(b.label);
  });

import metadata from "$data/meta.json";
/** @type {Array<MetricMeta>} */
const typedMetadata = /** @type {any} */ (metadata);
import labels from "$data/labels.json";

/** @typedef {import("$utils/types.d.ts").GeoLevel} GeoLevel */
/** @typedef {import("$utils/types.d.ts").Frequency} Frequency */
/** @typedef {import("$utils/types.d.ts").DollarType} DollarType */
/** @typedef {import("$utils/types.d.ts").MetricMeta} MetricMeta */
/** @typedef {import("$utils/types.d.ts").TimeSeriesPoint} TimeSeriesPoint */
/** @typedef {import("$utils/types.d.ts").TimeSeriesLayer} TimeSeriesLayer */
/** @typedef {import("$utils/types.d.ts").DataPoint} DataPoint */
/** @typedef {import("$utils/types.d.ts").Series} Series */
/** @typedef {import("$utils/types.d.ts").Metric} Metric */
/** @typedef {import("$utils/types.d.ts").SeriesOverview} SeriesOverview */

export class Data {
  labels = labels;
  /** @type {HTMLElement | undefined} */
  toolContainerEl = $state();
  /** @type {Array<DataPoint> | undefined} */
  nationalData = $state();
  /** @type {Array<DataPoint> | undefined} */
  stateData = $state();
  /** @type {Array<DataPoint> | undefined} */
  districtData = $state();
  dataReady = $derived(this.isReady());
  seriesList = $derived(this.getSeriesList());
  /** @type {Metric} */
  currentMetric = $state(DEFAULT_METRIC);
  /** @type {string | null} */
  currentDistrict = $state(null);
  /** @type {string} */
  currentState = $state("00");
  /** @type {GeoLevel} */
  currentLevel = $state("state");
  /** @type {DollarType} */
  currentDollarType = $state("nominal");
  currentNationalData = $derived.by(() =>
    this.getTimeSeries(GEO_LEVELS.NATIONAL, this.currentMetric, false)
  );
  currentChartData = $derived.by(() => this.getChartData());
  currentMapData = $derived.by(() => this.getMapData());
  currentMetadata = $derived.by(() => this.getMetadata(this.currentLevel, this.currentMetric));
  stateDropdownList = STATE_DROPDOWN_LIST;
  districtDropdownList = $derived.by(() => this.getDistrictList());
  seriesOverviews = $derived.by(() => this.getAllOverviews());
  levelMap = this.getLevelMap(typedMetadata);
  mostRecentDate = $derived.by(() => {
    return this.getMostRecentDate();
  });
  isCurrentMetricDollar = $derived.by(() => this.isDollarSeries(this.currentMetric));
  error = $state(null);
  /** @type {Set<string> | null} */
  _stateGeoids = $state(null);
  /** @type {Set<string> | null} */
  _districtGeoids = $state(null);
  /** @type {boolean} */
  _mounted = $state(false);

  constructor() {
    onMount(() => {
      // Read and apply URL params after mount to avoid SSR/hydration mismatch
      try {
        const rawParams = readParams();
        const validParams = validateParams(rawParams, SERIES_ORDER, idMap, this.levelMap);
        this.currentMetric = /** @type {Metric} */ (validParams.metric);
        this.currentLevel = /** @type {GeoLevel} */ (validParams.level);
        this.currentState = validParams.state;
        this.currentDistrict = validParams.district;
      } catch {
        // keep defaults
      }
      this._mounted = true;

      this.fetchData("national")
        .then((data) => {
          this.nationalData = data;
        })
        .catch((err) => {
          this.error = /** @type {any} */ (err).message || "Failed to load national data";
          console.error("Error loading national data:", err);
        });
      this.fetchData("state")
        .then((data) => {
          this.stateData = data;
          this._stateGeoids = this.getUniqueGeoids(data);
        })
        .catch((err) => {
          this.error = /** @type {any} */ (err).message || "Failed to load state data";
          console.error("Error loading state data:", err);
        });
      this.fetchData("cd")
        .then((data) => {
          this.districtData = data;
          this._districtGeoids = this.getUniqueGeoids(data);
        })
        .catch((err) => {
          this.error = /** @type {any} */ (err).message || "Failed to load district data";
          console.error("Error loading district data:", err);
        });

      // Listen for browser back/forward to restore state from URL
      const handlePopState = () => {
        try {
          const rawParams = readParams();
          const validParams = validateParams(rawParams, SERIES_ORDER, idMap, this.levelMap);
          this.currentMetric = /** @type {Metric} */ (validParams.metric);
          this.currentLevel = /** @type {GeoLevel} */ (validParams.level);
          this.currentState = validParams.state;
          this.currentDistrict = validParams.district;
        } catch {
          // ignore
        }
      };
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    });

    $effect(() => {
      //if level is state, set currentDistrict to none
      if (this.currentLevel === GEO_LEVELS.STATE) {
        this.currentDistrict = null;
      }
    });

    // if user changes state while district is selected, unset district
    $effect(() => {
      if (this.currentDistrict && this.currentState !== this.currentDistrict.slice(0, 2)) {
        this.currentDistrict = null;
      }
    });

    // if user selects a metric without district data, make sure states are active
    $effect(() => {
      if (
        this.currentLevel === GEO_LEVELS.DISTRICT &&
        !this.levelMap.get(/** @type {any} */ (this.currentMetric))?.has(GEO_LEVELS.DISTRICT)
      ) {
        this.currentLevel = GEO_LEVELS.STATE;
      }
    });

    // if user has selected a district, but that district doesn't exist in the current chart data, clear the selected district
    $effect(() => {
      // Don't validate district selection until data is loaded
      if (!this.currentDistrict || !this.districtData) return;

      let geoExists = false;
      for (let layer of this.currentChartData) {
        if (layer.level === GEO_LEVELS.DISTRICT) {
          // check if geoid exists in any data point
          geoExists = layer.data.some((d) =>
            Object.hasOwn(d, /** @type {string} */ (this.currentDistrict))
          );
        }
      }
      if (!geoExists) {
        this.currentDistrict = null;
      }
    });

    // Sync state changes to URL query params (only after mount)
    $effect(() => {
      // Access all reactive values to track them
      const metric = this.currentMetric;
      const state = this.currentState;
      const district = this.currentDistrict;
      const level = this.currentLevel;
      if (this._mounted) {
        writeParams({ metric, state, district, level });
      }
    });
  }

  /** Fetch data from a given URL
   * @param {string} level - the level of data to fetch (national, state, cd)
   * @returns {Promise<Array<DataPoint>>} - a promise that resolves to the fetched data
   */
  async fetchData(level) {
    const dataUrl = getAbsoluteUrl(`/${level}_data.csv`);
    try {
      const resp = await fetch(dataUrl);
      if (!resp.ok) {
        throw new Error(`Failed to fetch ${level} data: HTTP ${resp.status}`);
      }
      const text = await resp.text();
      const parsed = csvParse(text, this.parseCsvRow.bind(this));
      return parsed;
    } catch (err) {
      this.error = /** @type {any} */ (err).message || `Failed to load ${level} data`;
      console.error(`Error fetching ${level} data:`, err);
      return [];
    }
  }

  /** Check if all data is loaded and ready
   * @returns {boolean} - true if all data is loaded
   */
  isReady() {
    let isReady =
      this.nationalData &&
      this.nationalData.length > 0 &&
      this.stateData &&
      this.stateData.length > 0 &&
      this.districtData &&
      this.districtData.length > 0;
    return isReady || false;
  }

  /**
   * Get unique geoids from a dataset
   * @param {Array<DataPoint>} dataset - The dataset to extract geoids from
   * @returns {Set<string>} - Set of unique geoids
   */
  getUniqueGeoids(dataset) {
    if (!dataset || dataset.length === 0) return new Set();
    return new Set(dataset.map((item) => item.geoid));
  }

  /**
   * Format raw time series data into an array of level objects
   * Each level contains its own array of date objects with geoid keys
   * @param {GeoLevel} level - the level of data (national, state, cd)
   * @param {Metric} series - the series to use
   * @param {boolean} stateFilter - whether or not to filter out out of state geoids
   * @returns {Array<TimeSeriesLayer>} - array of level objects with time series data
   */
  getTimeSeries(level, series, stateFilter) {
    // Determine if this is a dollar series and get the correct series name
    const isDollarSeries = this.isDollarSeries(series);
    const seriesName = isDollarSeries ? `${series}_${this.currentDollarType}` : series;

    // Check if data is ready
    if (!this.dataReady) {
      return [];
    }

    const nationalData = /** @type {DataPoint[]} */ (this.nationalData);
    const stateData = /** @type {DataPoint[]} */ (this.stateData);
    const districtData = /** @type {DataPoint[]} */ (this.districtData);

    const result = [];

    // Helper function to merge data by date for a given dataset and set of geoids
    const mergeByDate = (
      /** @type {Array<DataPoint>} */ dataArray,
      /** @type {Set<string>} */ geoidsToInclude
    ) => {
      const dateMap = new Map();

      for (const item of dataArray) {
        // Skip if geoid not in include list
        if (!geoidsToInclude.has(item.geoid)) continue;
        // Skip if value is null
        if (item[seriesName] === null) continue;

        const dateStr = formatDate(item.date);
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, {
            date: item.date,
            series: seriesName
          });
        }
        dateMap.get(dateStr)[item.geoid] = item[seriesName];
      }

      // Convert map to sorted array
      return Array.from(dateMap.values()).sort((a, b) => a.date - b.date);
    };

    // Build the result based on the requested level
    if (level === GEO_LEVELS.NATIONAL) {
      // For national level, only include national data
      const natData = mergeByDate(nationalData, new Set(["00"]));
      if (natData.length > 0) {
        result.push({
          level: "national",
          data: natData
        });
      }
    } else if (level === GEO_LEVELS.STATE) {
      // For state level, include states and national for comparison

      // Add state data
      let stateGeoids;
      if (stateFilter && this.currentState && this.currentState !== "00") {
        // If filtering, only include current state
        stateGeoids = new Set([this.currentState]);
      } else {
        // Otherwise include all states (except national "00"); use cached set when available.
        const allStateGeoids = this._stateGeoids ?? this.getUniqueGeoids(stateData);
        stateGeoids = new Set(Array.from(allStateGeoids).filter((geoid) => geoid !== "00"));
      }

      const stData = mergeByDate(stateData, stateGeoids);
      if (stData.length > 0) {
        result.push({
          level: "state",
          data: stData
        });
      }

      // Add national data for comparison
      const natData = mergeByDate(nationalData, new Set(["00"]));
      if (natData.length > 0) {
        result.push({
          level: "national",
          data: natData
        });
      }
    } else if (level === GEO_LEVELS.DISTRICT) {
      // For district level, include districts, current state, and national

      // Add district data
      let districtGeoids;
      if (stateFilter && this.currentState) {
        // Filter to only the districts belonging to the current state.
        districtGeoids = new Set();
        for (const item of districtData) {
          if (item.geoid.startsWith(this.currentState)) {
            districtGeoids.add(item.geoid);
          }
        }
      } else {
        // Use cached set of all district geoids when available.
        districtGeoids = this._districtGeoids ?? this.getUniqueGeoids(districtData);
      }

      const distData = mergeByDate(districtData, districtGeoids);
      if (distData.length > 0) {
        result.push({
          level: "district",
          data: distData
        });
      }

      // Add state data (only current state if selected)
      if (this.currentState && this.currentState !== "00") {
        const stData = mergeByDate(stateData, new Set([this.currentState]));
        if (stData.length > 0) {
          result.push({
            level: "state",
            data: stData
          });
        }
      }

      // Add national data for comparison
      const natData = mergeByDate(nationalData, new Set(["00"]));
      if (natData.length > 0) {
        result.push({
          level: "national",
          data: natData
        });
      }
    }

    return /** @type {TimeSeriesLayer[]} */ (result);
  }

  /**
   * Get chart data for the current selections
   * @returns {Array<TimeSeriesLayer>} - Array of level objects with time series data
   */
  getChartData() {
    // For district level, apply state filter to only show districts from current state
    // For other levels, don't filter (or use current filter settings)
    const shouldFilter = this.currentLevel === "district";
    return this.getTimeSeries(this.currentLevel, this.currentMetric, shouldFilter);
  }

  /**
   * Get map data for the current selections
   * Maps need all geographies at a single point in time in a flat structure
   * @returns {TimeSeriesPoint} - Object with geoid keys and values for the most recent date
   */
  getMapData() {
    // Get all series for the current level (no state filter for maps)
    const levelArrays = this.getTimeSeries(this.currentLevel, this.currentMetric, false);

    // Find the data for the current level specifically.
    // Note: "cd" is only used as the URL path segment in fetchData; the level value is always "district".
    const currentLevelData = levelArrays.find((obj) => obj.level === this.currentLevel);

    if (!currentLevelData || currentLevelData.data.length === 0) {
      return { date: new Date(), series: this.currentMetric };
    }

    // Get the most recent entry from the current level's data
    const mostRecentEntry = currentLevelData.data[currentLevelData.data.length - 1];

    // Build the return object with all geoid values from the most recent date
    /** @type {any} */
    const mapData = {
      date: mostRecentEntry.date,
      series: this.currentMetric
    };

    // Copy all geoid values to mapData
    for (const [key, value] of Object.entries(mostRecentEntry)) {
      // Skip metadata keys
      if (key === "date" || key === "series" || key === "level") continue;
      mapData[key] = value;
    }

    return mapData;
  }

  /**
   * Get series overview for a specific series
   * Used for the series overview cards/list
   * @param {Metric} series - The series name
   * @returns {SeriesOverview | undefined} - Overview object with metadata and recent value
   */
  getSeriesOverview(series) {
    const timeSeries = this.getTimeSeries(GEO_LEVELS.NATIONAL, series, false);
    const seriesMeta = this.getMetadata(GEO_LEVELS.NATIONAL, series);
    if (!seriesMeta) {
      return undefined;
    }

    // Get the national level data
    const nationalLevel = timeSeries.find((level) => level.level === "national");
    if (!nationalLevel || nationalLevel.data.length === 0) {
      return {
        series,
        label: series,
        units: seriesMeta.units,
        display_format: seriesMeta.display_format,
        timeSeries: [],
        category: seriesMeta.category,
        recentValue: null
      };
    }

    const recentEntry = nationalLevel.data[nationalLevel.data.length - 1];

    return {
      series,
      label: seriesMeta.long_label,
      units: seriesMeta.units,
      display_format: seriesMeta.display_format,
      timeSeries: nationalLevel.data,
      category: seriesMeta.category,
      recentValue: /** @type {number | null} */ (recentEntry["00"])
    };
  }

  /**
   * Get overviews for all series, ordered by category
   * @returns {Array<SeriesOverview | undefined>} - Array of series overview objects
   */
  getAllOverviews() {
    // return overview for all series, ordered by category
    return this.seriesList
      .map((series) => this.getSeriesOverview(series))
      .toSorted((a, b) => {
        if (!a || !b) return 0;
        return a.category.localeCompare(b.category);
      });
  }

  /** Format a date object into a string for display or storage
   * @param {Date} realDate - the date object to format
   * @returns {string} - the formatted date string
   */
  formatDate(realDate) {
    return formatDate(realDate);
  }

  /** Parse a date string into a date object
   * @param {string} strDate - the date string to parse
   * @returns {Date | null} - the parsed date object
   */
  parseDate(strDate) {
    return parseDate(strDate, FREQUENCIES.INPUT);
  }

  /**
   * Get metadata for a given level and series
   * @param { string } level - the level of data (national, state, cd)
   * @param { string } series - the series name to fetch
   * @returns { MetricMeta | undefined } - the metadata object or null if not found
   */
  getMetadata(level, series) {
    let meta = typedMetadata.find((d) => d.series === series && d.geography === level);
    return meta;
  }

  /**
   * Get the short label for a given series ID.
   * @param {string} series
   * @returns {string}
   */
  getMetricLabel(series) {
    const entry = typedMetadata.find((m) => m.series === series);
    return entry?.short_label ?? series;
  }

  /**
   *@returns {Array<Metric>} - list of all unique series names from metadata
   */
  getSeriesList() {
    return SERIES_ORDER;
  }

  /**
   * Check if a series is a dollar type series
   * @param {Metric} series - the series name to check
   * @returns {boolean} - true if the series is a dollar type, false otherwise
   */
  isDollarSeries(series) {
    let meta = this.getMetadata(GEO_LEVELS.NATIONAL, series);
    return meta ? meta.units === "dollars" : false;
  }

  /** @param {string} geoid */
  setDistrict(geoid) {
    this.currentDistrict = geoid;
  }

  /** @param {GeoLevel} level */
  setLevel(level) {
    this.currentLevel = level;
  }

  /**
   * Parse a CSV row into a structured object
   * @param {Object<string, string>} row - the CSV row object
   * @returns {DataPoint} - the parsed row object
   */
  parseCsvRow(row) {
    let skip_cols = ["geoid", "date", "level"];
    /** @type {any} */
    let result = {
      geoid: row.geoid,
      date: this.parseDate(row.date) ?? new Date(NaN),
      level: row.level
    };
    for (let col in row) {
      if (skip_cols.includes(col)) continue;
      let val = Number(row[col]);
      result[col] = isNaN(val) ? null : val;
    }
    return /** @type {DataPoint} */ (result);
  }

  /** Get a list of districts
   * @returns {Array<{value: string, label: string}>} - list of district options
   */
  getDistrictList() {
    if (!this.districtData) return [];
    return Array.from(new Set(this.districtData.map(({ geoid }) => geoid))).map((geoid) => ({
      value: geoid,
      label: getCdAbb(geoid)
    }));
  }

  /**
   * Retrieve necessary data points for a metric/geo combo's descriptive text
   * @param { GeoLevel } level
   * @param { string } geoid
   * @param { Metric } metric
   * @returns { Record<string, string | null> } - key-value pairs of metric variables and their values
   */
  /**
   * Shared setup for description data methods.
   * Resolves the effective level/geoid, fetches metadata and time series, and finds the most
   * recent non-null value for the requested geoid.
   * @param {GeoLevel} level
   * @param {string} geoid
   * @param {Metric} metric
   * @returns {{ metricInfo: MetricMeta, metricData: Array<TimeSeriesLayer>, levelSeries: TimeSeriesLayer, recentValue: number | null, recentDate: Date | null, realLevel: GeoLevel, realGeoid: string } | null}
   */
  _resolveDescriptionContext(level, geoid, metric) {
    if (!this.dataReady) return null;

    // Sometimes current level is "state" but the active geo is really the national number.
    // Likewise, level may be "district" but only the state is selected.
    let realLevel = /** @type {GeoLevel} */ (geoid === "00" ? GEO_LEVELS.NATIONAL : level);
    let realGeoid = geoid;
    if (level === GEO_LEVELS.DISTRICT && !this.currentDistrict && this.currentState !== "00") {
      realLevel = GEO_LEVELS.STATE;
      realGeoid = this.currentState;
    }

    const metricInfo = this.getMetadata(realLevel, metric);
    const metricData = this.getTimeSeries(realLevel, metric, false);
    const levelSeries = metricData.find((d) => d.level === realLevel);
    if (!levelSeries || !metricInfo) return null;

    let recentValue = /** @type {number | null} */ (null);
    let recentDate = /** @type {Date | null} */ (null);
    for (let i = levelSeries.data.length - 1; i >= 0; i--) {
      if (levelSeries.data[i][realGeoid] !== null) {
        recentValue = /** @type {number | null} */ (levelSeries.data[i][realGeoid]);
        recentDate = /** @type {Date} */ (levelSeries.data[i].date);
        break;
      }
    }

    return { metricInfo, metricData, levelSeries, recentValue, recentDate, realLevel, realGeoid };
  }

  /**
   * Retrieve necessary data points for a metric/geo combo's descriptive text
   * @param { GeoLevel } level
   * @param { string } geoid
   * @param { Metric } metric
   * @returns { Record<string, string | null> } - key-value pairs of metric variables and their values
   */
  getDescriptionData(level, geoid, metric) {
    const ctx = this._resolveDescriptionContext(level, geoid, metric);
    if (!ctx) return {};

    const { metricInfo, levelSeries, recentValue, recentDate, realGeoid } = ctx;

    // find comparison points by searching back through the time series to find the next non-null data point
    let firstPoint = this.findComparisonDataPoint(levelSeries.data, metricInfo.frequency, metricInfo.comparison_mode || null);
    let firstDate = firstPoint?.date ?? null;
    let firstValue = /** @type {number | null} */ (firstPoint?.[realGeoid] ?? null);

    const pctChange =
      firstValue != null && recentValue != null
        ? Math.abs((recentValue - firstValue) / firstValue)
        : null;

    return {
      currentMetric: metricInfo.long_label.toLowerCase(),
      currentValue: formatValue(recentValue, metricInfo),
      currentDate: recentDate ? formatDate(recentDate, metricInfo.frequency) : null,
      firstValue: firstValue != null ? formatValue(firstValue, metricInfo) : null,
      trendDirection:
        firstValue != null && recentValue != null && firstValue > recentValue ? "down" : "up",
      pctChange: pctChange != null ? formatChange(pctChange) : null,
      currentGeo: getDescriptionName(realGeoid),
      firstDate: firstDate ? formatDate(firstDate, metricInfo.frequency) : null,
      descriptionTemplate: metricInfo.description_template || null,
      headerTemplate: metricInfo.header_template || null
    };
  }

  /**
   * Create a map of available levels for each series from metadata
   * @param {Array<MetricMeta>} meta - the metadata array
   * @returns {Map<Series, Set<GeoLevel>>} - map of series to available levels
   */
  getLevelMap(meta) {
    const levelMap = new Map();
    for (const { series, geography } of meta) {
      if (!levelMap.has(series)) {
        levelMap.set(series, new Set());
      }
      levelMap.get(series).add(geography);
    }
    return levelMap;
  }
  /**
   * Retrieve necessary data points for a metric/geo combo's descriptive text
   * @param { GeoLevel } level
   * @param { string } geoid
   * @param { Metric } metric
   * @returns { Record<string, string | null> } - key-value pairs of metric variables and their values
   */
  getMapDescriptionData(level, geoid, metric) {
    const ctx = this._resolveDescriptionContext(level, geoid, metric);
    if (!ctx) return {};

    const { metricInfo, metricData, recentValue, recentDate, realLevel } = ctx;

    // find the national comparison value from the same time series call
    let nationalSeries = metricData.find((d) => d.level === GEO_LEVELS.NATIONAL);
    let nationalPoint = nationalSeries?.data[nationalSeries.data.length - 1];
    let nationalValue = /** @type {number | null} */ (nationalPoint?.["00"] ?? null);

    return {
      currentMetric: metricInfo.long_label.toLowerCase(),
      currentValue: formatValue(recentValue, metricInfo),
      currentDate: recentDate ? formatDate(recentDate, metricInfo.frequency) : null,
      nationalValue:
        nationalValue && realLevel !== GEO_LEVELS.NATIONAL
          ? formatValue(nationalValue, metricInfo)
          : null,
      currentGeo: getDescriptionName(geoid),
      descriptionTemplate: metricInfo.description_template || null,
      headerTemplate: metricInfo.header_template || null
    };
  }

  /**
   * Finds the comparison data point for the most recent point in a time series.
   *
   * For annual data: returns the previous year's data point
   * For sub-annual data: returns the first data point of the current year,
   *   unless the current point is already the first of its year, in which case
   *   it returns the first point of the previous year
   *
   * @param {TimeSeriesPoint[]} timeSeries - Array of time series data points
   * @param {Frequency} frequency - The frequency of the data
   * @param {"year_prior" | null} comparisonMode - Whether or not to force a comparison to prior year
   * @returns {TimeSeriesPoint | null} The comparison data point, or null if not found
   */
  findComparisonDataPoint(timeSeries, frequency, comparisonMode) {
    if (!timeSeries || timeSeries.length === 0) {
      return null;
    }

    // Data is already sorted ascending by mergeByDate; no re-sort needed.
    const sortedSeries = timeSeries;

    const mostRecent = sortedSeries[sortedSeries.length - 1];
    const mostRecentYear = mostRecent.date.getFullYear();

    // For annual data, find the previous year
    if (frequency === FREQUENCIES.ANNUAL) {
      const targetYear = mostRecentYear - 1;
      return (
        sortedSeries.find((point) => {
          return point.date.getFullYear() === targetYear;
        }) || null
      );
    }

    // For sub-annual data with year_prior override
    if (comparisonMode === "year_prior") {
      // Find the point from exactly 12 months before the most recent point
      const targetDate = new Date(mostRecent.date);
      targetDate.setFullYear(targetDate.getFullYear() - 1);
      return (
        sortedSeries.find(
          (point) => point.date.getTime() === targetDate.getTime()
        ) || null
      );
    }

    // For sub-annual data
    if (
      [FREQUENCIES.MONTHLY, FREQUENCIES.WEEKLY, FREQUENCIES.QUARTERLY, FREQUENCIES.OTHER].includes(
        frequency
      )
    ) {
      // Find the first data point in the current year
      const firstPointInCurrentYear = sortedSeries.find((point) => {
        return point.date.getFullYear() === mostRecentYear;
      });

      // Check if the most recent point is the first point of its year
      const isFirstOfYear =
        firstPointInCurrentYear &&
        mostRecent.date.getTime() === firstPointInCurrentYear.date.getTime();

      if (isFirstOfYear) {
        // If current is first of year, get first of previous year
        const targetYear = mostRecentYear - 1;
        return (
          sortedSeries.find((point) => {
            return point.date.getFullYear() === targetYear;
          }) || null
        );
      } else {
        // Otherwise, return the first point of current year
        return firstPointInCurrentYear ?? null;
      }
    }

    // For other frequencies, return the first value or null
    return timeSeries[0] || null;
  }

  /** Get the most recent date across all loaded datasets.
   * @returns {Date | null}
   */
  getMostRecentDate() {
    if (!this.dataReady) return null;
    // Single-pass over all datasets to find the maximum date without building intermediate arrays.
    let maxTime = -Infinity;
    for (const dataset of [this.nationalData, this.stateData, this.districtData]) {
      if (dataset && dataset.length > 0) {
        for (const d of dataset) {
          const t = d.date.getTime();
          if (t > maxTime) maxTime = t;
        }
      }
    }
    return maxTime === -Infinity ? null : new Date(maxTime);
  }
}

const contextKey = Symbol("dataContext");

/** @returns {Data} */
export function createData() {
  const data = new Data();
  setContext(contextKey, data);
  return data;
}

/** @returns {Data} */
export function useData() {
  return getContext(contextKey);
}
